import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, Divider,
  Chip, Grid, TextField, Alert, Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dealersApi, type Dealer } from '../../../api/dealers.api';
import { extractApiError } from '../../../api/error';
import { StatusBadge } from '../../../components/StatusBadge';
import { useAuth } from '../../../hooks/useAuth';

interface Props {
  dealer: Dealer | null;
  onClose: () => void;
  onChanged: () => void;
}

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;

export function DealerDetailDialog({ dealer, onClose, onChanged }: Props) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const open = dealer !== null;
  const id = dealer?.id ?? 0;
  const [rejectReason, setRejectReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [credential, setCredential] = useState<string | null>(null);

  const { data: history = [] } = useQuery({
    queryKey: ['dealer-history', id],
    queryFn: () => dealersApi.history(id),
    enabled: open,
  });

  const done = () => { setError(null); onChanged(); qc.invalidateQueries({ queryKey: ['dealer-history', id] }); };
  const onErr = (e: unknown) => setError(extractApiError(e));

  const submitMut = useMutation({ mutationFn: () => dealersApi.submit(id), onSuccess: done, onError: onErr });
  const approveMut = useMutation({
    mutationFn: () => dealersApi.approve(id),
    onSuccess: (d) => { if (d.temporaryPassword) setCredential(d.temporaryPassword); done(); },
    onError: onErr,
  });
  const rejectMut = useMutation({ mutationFn: () => dealersApi.reject(id, rejectReason), onSuccess: done, onError: onErr });
  const provisionMut = useMutation({
    mutationFn: () => dealersApi.provisionLogin(id),
    onSuccess: (r) => { setCredential(r.temporaryPassword); done(); },
    onError: onErr,
  });
  const suspendMut = useMutation({ mutationFn: () => dealersApi.setStatus(id, 'suspended'), onSuccess: done, onError: onErr });
  const reactivateMut = useMutation({ mutationFn: () => dealersApi.setStatus(id, 'approved'), onSuccess: done, onError: onErr });

  if (!dealer) return null;

  const canApprove = hasPermission('dealer.approve');
  const canSubmit = hasPermission('dealer.submit');
  const canManage = hasPermission('dealer.update');

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <span>{dealer.dealerName}</span>
          <Chip size="small" label={dealer.dealerCode} variant="outlined" />
          <StatusBadge status={dealer.status} />
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {credential && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCredential(null)}>
            Dealer login provisioned. Temporary password: <strong>{credential}</strong> — share securely; it is shown only once.
          </Alert>
        )}
        <Grid container spacing={1.5} mb={2}>
          <Grid item xs={6} sm={4}><Typography variant="caption" color="text.secondary">Type</Typography><Typography>{dealer.dealerType}</Typography></Grid>
          <Grid item xs={6} sm={4}><Typography variant="caption" color="text.secondary">GST</Typography><Typography>{dealer.gstNumber ?? '—'}</Typography></Grid>
          <Grid item xs={6} sm={4}><Typography variant="caption" color="text.secondary">PAN</Typography><Typography>{dealer.panNumber ?? '—'}</Typography></Grid>
          <Grid item xs={6} sm={4}><Typography variant="caption" color="text.secondary">Email</Typography><Typography>{dealer.email ?? '—'}</Typography></Grid>
          <Grid item xs={6} sm={4}><Typography variant="caption" color="text.secondary">Mobile</Typography><Typography>{dealer.mobile ?? '—'}</Typography></Grid>
          <Grid item xs={6} sm={4}><Typography variant="caption" color="text.secondary">Credit Limit</Typography><Typography>{inr(dealer.creditLimit)} / {dealer.creditDays}d</Typography></Grid>
          <Grid item xs={6} sm={4}><Typography variant="caption" color="text.secondary">Has Login</Typography><Typography>{dealer.hasLogin ? 'Yes' : 'No'}</Typography></Grid>
          {dealer.rejectionReason && (
            <Grid item xs={12}><Alert severity="warning">Rejected: {dealer.rejectionReason}</Alert></Grid>
          )}
        </Grid>

        <Divider textAlign="left" sx={{ mb: 1 }}>Approval Status History</Divider>
        <Table size="small">
          <TableHead><TableRow><TableCell>Action</TableCell><TableCell>From → To</TableCell><TableCell>Remarks</TableCell><TableCell>When</TableCell></TableRow></TableHead>
          <TableBody>
            {history.map((h) => (
              <TableRow key={h.id}>
                <TableCell>{h.action}</TableCell>
                <TableCell>{h.fromStatus ?? '—'} → {h.toStatus}</TableCell>
                <TableCell>{h.remarks ?? '—'}</TableCell>
                <TableCell>{new Date(h.createdAt).toLocaleString()}</TableCell>
              </TableRow>
            ))}
            {history.length === 0 && <TableRow><TableCell colSpan={4}>No history yet.</TableCell></TableRow>}
          </TableBody>
        </Table>

        {dealer.status === 'pending_approval' && canApprove && (
          <Stack spacing={1} mt={2}>
            <Divider textAlign="left">Decision</Divider>
            <TextField size="small" label="Rejection reason (required to reject)" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} fullWidth />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {(dealer.status === 'draft' || dealer.status === 'rejected') && canSubmit && (
          <Button variant="contained" onClick={() => submitMut.mutate()} disabled={submitMut.isPending}>Submit for Approval</Button>
        )}
        {dealer.status === 'pending_approval' && canApprove && (
          <>
            <Button color="success" variant="contained" onClick={() => approveMut.mutate()} disabled={approveMut.isPending}>Approve</Button>
            <Button color="error" variant="outlined" disabled={!rejectReason || rejectMut.isPending} onClick={() => rejectMut.mutate()}>Reject</Button>
          </>
        )}
        {dealer.status === 'approved' && !dealer.hasLogin && canManage && (
          <Button variant="outlined" onClick={() => provisionMut.mutate()} disabled={provisionMut.isPending}>Provision Login</Button>
        )}
        {dealer.status === 'approved' && canManage && (
          <Button color="warning" variant="outlined" onClick={() => suspendMut.mutate()} disabled={suspendMut.isPending}>Suspend</Button>
        )}
        {dealer.status === 'suspended' && canManage && (
          <Button color="success" variant="outlined" onClick={() => reactivateMut.mutate()} disabled={reactivateMut.isPending}>Re-activate</Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
